import { useRef, useState, useCallback, useEffect } from 'react';
import { calcEAR, calcMAR, calcGaze, calcHeadPose, calcBrowMetrics,
         calcLipMetrics, calcJawMetrics, calcChinMetrics,
         calcAttentionScore } from '../engine/metrics';

// ── Thresholds ────────────────────────────────────────────────────────────────
const CALIB_NEEDED     = 15;
const CLOSED_RATIO     = 0.60;
const SLEEPY_RATIO     = 0.72;
const TIRED_RATIO      = 0.84;
const ENERGETIC_RATIO  = 1.08;
const MAR_YAWN         = 0.55;
const YAWN_FRAMES_MIN  = 3;
const SQUINT_FRAMES    = 4;
const GAZE_YAW_THRESH  = 0.30;
const GAZE_PITCH_UP    = -0.45;

// MediaPipe landmark coords are normalised 0-1 (not pixels).
// faceScale() returns fraction of image height (~0.4-0.7 typically).
// All geometric ratios are therefore small fractions — tuned empirically.
const BROW_FURROW = 0.040;   // browLift below = furrowed
const BROW_RAISED = 0.075;   // browLift above = raised
const BROW_ASYM   = 0.008;   // asymmetry threshold
const INNER_BROW  = 0.045;   // innerBrowDist below = pinched
const SMILE_T     = 0.006;   // smileScore above = smile geometry confirmed
const LIP_PRESS_T = 0.22;    // innerLipH below = pressed (this is a ratio, stays same)
const LIP_CURL_T  = 0.008;   // upperLipCurl above = sneer
const LIP_ASYM_T  = 0.006;   // lipAsym above = smirk
const JAW_CLENCH  = 0.80;    // jawClenchRatio below = clenched
const CHIN_TUCK   = 0.18;    // chinToNose below = tucked (normalised)

const DISTRACT_PROFILES = {
  single:  { yaw: 0.30, pitch: 0.45 },
  dual:    { yaw: 0.75, pitch: 0.50 },
  triple:  { yaw: 0.95, pitch: 0.50 },
  stacked: { yaw: 0.65, pitch: 9.99 },
};

// ── Emotion classifier using geometry + face-api expressions ─────────────────
function classifyEmotion({ faceApiEmotion, expressions, brow, lip, jaw, chin, gaze,
    ear, baseline, bpm, perclos, yawning, squinting, singleScreen, cheekPuff, headYaw, monitorSetup }) {
  const { browLift, browAsym, innerBrowDist } = brow;
  const { smileScore, lipAsym, innerLipH, upperLipCurl } = lip;

  if (yawning) return 'yawning';
  if (lipAsym > LIP_ASYM_T && smileScore > 0 && faceApiEmotion !== 'sad') return 'smirking';
  if (upperLipCurl > LIP_CURL_T && browLift < BROW_FURROW) return 'disgusted';
  if (innerLipH < LIP_PRESS_T && jaw.jawClenchRatio < JAW_CLENCH && !['happy','surprised'].includes(faceApiEmotion)) return 'tense';
  if (singleScreen && bpm !== null && bpm < 3 && ear > (baseline||0.26)*0.9 && Math.abs(gaze.horizontal) < 0.2 && Math.abs(gaze.vertical||0) < 0.3 && (faceApiEmotion==='neutral'||faceApiEmotion==='sad')) return 'zoned_out';
  if (cheekPuff && !yawning) return 'tense';  // holding breath = tense
  if (innerBrowDist < INNER_BROW && browLift < BROW_FURROW && bpm !== null && bpm > 18) return 'stressed';
  if (chin.chinToNose < CHIN_TUCK && gaze.vertical > 0.25 && smileScore < 0 && (faceApiEmotion==='sad'||faceApiEmotion==='neutral')) return 'dejected';
  if (squinting && browLift < BROW_RAISED) return 'squinting';
  if (browLift < BROW_FURROW && innerBrowDist < INNER_BROW + 0.02 && !['happy','surprised','angry','fearful'].includes(faceApiEmotion)) return 'concentrating';
  // Suppress 'thinking' when head is turning to look at another screen
  // Brow asymmetry is a natural side effect of head rotation, not actual thought
  const screenTurnYaw = monitorSetup === 'stacked' ? 0.30 : monitorSetup === 'dual' ? 0.35 : 0.99;
  const isTurningToScreen = Math.abs(headYaw || 0) > screenTurnYaw;
  if (browAsym > BROW_ASYM && browLift > BROW_FURROW && faceApiEmotion==='neutral' && !isTurningToScreen) return 'thinking';
  if (isTurningToScreen && browLift < BROW_FURROW + 0.02 && !yawning) return 'concentrating';
  if (faceApiEmotion==='happy' && smileScore > SMILE_T) return 'happy';
  if (browLift > BROW_FURROW && browLift < BROW_RAISED && innerLipH > LIP_PRESS_T && (faceApiEmotion==='neutral'||faceApiEmotion==='happy') && (bpm===null||bpm<15) && perclos < 0.05) return 'calm';

  // Surprised only fires if BOTH face-api says so AND brows are genuinely raised
  // (prevents false positives from normal resting wide-eye expressions)
  if (faceApiEmotion==='surprised' && browLift > BROW_RAISED && innerLipH > 0.15) return 'surprised';

  // Angry only if geometry supports it (brow furrow + jaw)
  if (faceApiEmotion==='angry' && browLift < BROW_FURROW && jaw.jawClenchRatio < JAW_CLENCH) return 'angry';

  // If face-api gives a low-confidence reading, fall back to geometry-derived neutral/calm
  const faceApiConf = expressions ? Math.max(...Object.values(expressions)) : 1;
  if (faceApiConf < 0.55 && perclos < 0.08 && Math.abs(gaze?.horizontal||0) < 0.25) return 'calm';
  if (faceApiConf < 0.55) return 'neutral';

  // Trust face-api for remaining cases: sad, fearful, disgusted
  if (['sad','fearful','disgusted'].includes(faceApiEmotion)) return faceApiEmotion;
  return 'neutral';
}

// ── Energy level ──────────────────────────────────────────────────────────────
function getEnergyLevel(earAvg, baseline, perclos, bpm) {
  const b = baseline || 0.28;
  if (perclos > 0.20 || earAvg < b * SLEEPY_RATIO)   return 'sleepy';
  if (perclos > 0.10 || earAvg < b * TIRED_RATIO)    return 'tired';
  if (bpm !== null && bpm > 25)                       return 'tired';
  if (bpm !== null && bpm < 4)                        return 'energetic';
  if (earAvg >= b * ENERGETIC_RATIO)                  return 'energetic';
  return 'alert';
}

// ═══════════════════════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════════════════════
export function useDetection({ onSnapshot, onLiveMetrics, monitorSetup = 'single' }) {
  // ── State exposed to UI ────────────────────────────────────────────────────
  const [ready,          setReady]          = useState(false);
  const [facePresent,    setFacePresent]    = useState(false);
  const [emotion,        setEmotion]        = useState(null);
  const [expressions,    setExpressions]    = useState(null);
  const [energy,         setEnergy]         = useState(null);
  const [attentionScore, setAttentionScore] = useState(null);
  const [ear,            setEar]            = useState(null);
  const [blinkCount,     setBlinkCount]     = useState(0);
  const [blinkRate,      setBlinkRate]      = useState(null);
  const [blinkDurationAvg, setBlinkDurationAvg] = useState(null);
  const [perclos,        setPerclos]        = useState(0);
  const [gaze,           setGaze]           = useState(null);
  const [headPose,       setHeadPose]       = useState(null);
  const [focusState,     setFocusState]     = useState(null);
  const [focusPct,       setFocusPct]       = useState(null);
  const [focusedMs,      setFocusedMs]      = useState(0);
  const [distractedMs,   setDistractedMs]   = useState(0);
  const [calibrated,     setCalibrated]     = useState(false);
  const [calibProgress,  setCalibProgress]  = useState(0);
  const [yawnCount,      setYawnCount]      = useState(0);

  // ── Refs (no re-render needed) ─────────────────────────────────────────────
  const mpRef          = useRef(null);   // MediaPipe FaceLandmarker
  const faceapiRef     = useRef(null);   // face-api expression net
  const rafRef         = useRef(null);   // requestAnimationFrame id
  const lastEmotionRef    = useRef(null);   // throttle emotion classification
  const emotionBufferRef  = useRef([]);      // smoothing: require consistency before switching
  const stableEmotionRef  = useRef(null);    // last confirmed stable emotion

  // Blink tracking
  const earHistory      = useRef([]);    // { t, ear }
  const blinkTimestamps = useRef([]);
  const blinkDurations  = useRef([]);
  const eyeState        = useRef('open');
  const blinkOpenT      = useRef(null);
  const blinkCountRef   = useRef(0);

  // Calibration
  const calibSamples  = useRef([]);
  const baselineRef   = useRef(null);

  // Sustained state counters
  const yawnFrames   = useRef(0);
  const squintFrames = useRef(0);
  const yawnCountRef = useRef(0);

  // Focus tracking
  const focusStartRef    = useRef(null);
  const distractStartRef = useRef(null);
  const totalFocusRef    = useRef(0);
  const energyBufferRef  = useRef([]);   // smoothing buffer for energy
  const attnBufferRef    = useRef([]);   // smoothing buffer for attention
  const totalDistractRef = useRef(0);
  const lastFocusRef     = useRef(null);
  const focusSaveRef     = useRef(null); // interval for persisting focus ms

  // Snapshot accumulation (for 60s snapshots)
  const snapshotWindowRef = useRef([]);
  const lastSnapshotRef   = useRef(Date.now());
  const snapshotYawnRef   = useRef(0);

  // Latest metrics ref (for snapshot builder)
  const latestMetrics = useRef({});

  // ── Load MediaPipe ────────────────────────────────────────────────────────
  const loadMediaPipe = useCallback(async () => {
    let FaceLandmarker, FilesetResolver;
    try {
      const mod = await import(
        'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.12/vision_bundle.mjs'
      );
      FaceLandmarker = mod.FaceLandmarker;
      FilesetResolver = mod.FilesetResolver;
    } catch(e) {
      console.error('MediaPipe bundle import failed:', e);
      return false;
    }

    let vision;
    try {
      vision = await FilesetResolver.forVisionTasks(
        'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.12/wasm'
      );
    } catch(e) {
      console.error('FilesetResolver failed:', e);
      return false;
    }

    // Try GPU first, fall back to CPU
    const MODEL_URL = 'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/latest/face_landmarker.task';
    for (const delegate of ['GPU', 'CPU']) {
      try {
        const landmarker = await FaceLandmarker.createFromOptions(vision, {
          baseOptions: { modelAssetPath: MODEL_URL, delegate },
          outputFaceBlendshapes: false,
          runningMode: 'VIDEO',
          numFaces: 1,
        });
        mpRef.current = landmarker;
        console.log('MediaPipe loaded with delegate:', delegate);
        return true;
      } catch(e) {
        console.warn('MediaPipe load attempt failed (' + delegate + '):', e.message);
      }
    }
    console.error('All MediaPipe load attempts failed');
    return false;
  }, []);

  // ── Load face-api (expression net only) ──────────────────────────────────
  const loadFaceApi = useCallback(async () => {
    let attempts = 0;
    while (!window.faceapi && attempts < 30) { await new Promise(r => setTimeout(r, 300)); attempts++; }
    if (!window.faceapi) return false;
    try {
      const faceapi = window.faceapi;
      faceapiRef.current = faceapi;
      const URLS = [
        'https://cdn.jsdelivr.net/npm/@vladmandic/face-api@1.7.12/model',
        'https://cdn.jsdelivr.net/npm/face-api.js@0.22.2/weights',
      ];
      for (const url of URLS) {
        try {
          // Only load expression net — skip the rest to save bandwidth
          await faceapi.nets.tinyFaceDetector.loadFromUri(url);
          await faceapi.nets.faceExpressionNet.loadFromUri(url);
          return true;
        } catch(e) { console.warn('face-api URL failed:', e.message); }
      }
      return false;
    } catch(e) { return false; }
  }, []);

  // ── Initialise both engines in parallel ──────────────────────────────────
  const init = useCallback(async () => {
    const [mpOk, faOk] = await Promise.all([loadMediaPipe(), loadFaceApi()]);
    if (mpOk) setReady(true);
    return mpOk;
  }, [loadMediaPipe, loadFaceApi]);

  // ── Per-frame blink processor ─────────────────────────────────────────────
  function processBlink(earAvg, now) {
    const baseline = baselineRef.current;
    const closedT  = baseline ? baseline * CLOSED_RATIO : 0.17;

    if (earAvg < closedT && eyeState.current === 'open') {
      eyeState.current = 'closed';
      blinkOpenT.current = now;
    } else if (earAvg >= closedT && eyeState.current === 'closed') {
      eyeState.current = 'open';
      const dur = blinkOpenT.current ? now - blinkOpenT.current : 0;
      if (dur > 40 && dur < 600) {   // valid blink 40–600ms
        blinkCountRef.current++;
        blinkTimestamps.current.push(now);
        blinkDurations.current.push(dur);
        setBlinkCount(blinkCountRef.current);
      }
    }

    earHistory.current.push({ t: now, ear: earAvg });
    earHistory.current = earHistory.current.filter(p => now - p.t < 60000);
  }

  // ── Main frame loop ───────────────────────────────────────────────────────
  const runFrame = useCallback((videoEl, canvasEl, timestamp, showMesh) => {
    const mp = mpRef.current;
    if (!mp || !videoEl || videoEl.paused) return;

    const ctx  = canvasEl.getContext('2d');
    const now  = Date.now();

    // ── MediaPipe detection ────────────────────────────────────────────────
    let lm = null;
    let bs = null;
    try {
      const result = mp.detectForVideo(videoEl, timestamp);
      lm = result?.faceLandmarks?.[0] ?? null;
      // bs (blendshapes) will be populated if model supports it
      const rawBs = result?.faceBlendshapes?.[0]?.categories;
      if (rawBs && rawBs.length > 0) {
        bs = {};
        rawBs.forEach(b => { bs[b.categoryName] = b.score; });
      }
    } catch(e) { lm = null; }

    ctx.clearRect(0, 0, canvasEl.width, canvasEl.height);
    // Mirror canvas to match the CSS-mirrored video element
    ctx.save();
    ctx.translate(canvasEl.width, 0);
    ctx.scale(-1, 1);

    if (!lm) {
      ctx.restore();
      setFacePresent(false);
      setEmotion(null);
      latestMetrics.current.facePresent = false;
      return;
    }

    setFacePresent(true);

    // ── Compute all metrics ────────────────────────────────────────────────
    const earResult  = calcEAR(lm);
    // If blendshapes available, use trained blink scores (much more accurate with glasses)
    const bsBlinkL   = bs?.eyeBlinkLeft  ?? null;
    const bsBlinkR   = bs?.eyeBlinkRight ?? null;
    const bsEarAvg   = (bsBlinkL !== null && bsBlinkR !== null)
      ? 1 - (bsBlinkL + bsBlinkR) / 2   // blendshape 0=open, 1=closed → invert to match EAR convention
      : null;
    const earAvg     = bsEarAvg !== null ? bsEarAvg * 0.4 : earResult.avg;  // scale to ~0.2-0.4 range
    const mar        = bs?.jawOpen ?? calcMAR(lm);
    const gazeResult = calcGaze(lm);
    const pose       = calcHeadPose(lm);
    const brow       = calcBrowMetrics(lm);
    // Augment brow with blendshape signals
    if (bs) {
      const browDownAvg = ((bs.browDownLeft||0) + (bs.browDownRight||0)) / 2;
      const browUpInner = bs.browInnerUp || 0;
      // Override computed values with blendshape equivalents when available
      brow.browLift     = 0.06 - browDownAvg * 0.06 + browUpInner * 0.04;
      brow.innerBrowDist = 0.06 - browUpInner * 0.04;
    }
    const lip        = calcLipMetrics(lm);
    if (bs) {
      const smileL = bs.mouthSmileLeft  || 0;
      const smileR = bs.mouthSmileRight || 0;
      const frownL = bs.mouthFrownLeft  || 0;
      const frownR = bs.mouthFrownRight || 0;
      lip.smileScore  = ((smileL + smileR) - (frownL + frownR)) / 2 * 0.05;
      lip.lipAsym     = Math.abs(smileL - smileR) * 0.05;
      lip.upperLipCurl = (bs.noseSneerLeft||0 + bs.noseSneerRight||0) / 2 * 0.03;
    }
    const jaw        = calcJawMetrics(lm);
    const chin       = calcChinMetrics(lm);

    processBlink(earAvg, now);

    // EAR calibration
    const baseline = baselineRef.current;
    if (!baseline && earAvg > 0.15) {
      calibSamples.current.push(earAvg);
      const prog = Math.round(Math.min(calibSamples.current.length / CALIB_NEEDED, 1) * 100);
      setCalibProgress(prog);
      if (calibSamples.current.length >= CALIB_NEEDED) {
        const sorted = [...calibSamples.current].sort((a,b) => a-b);
        baselineRef.current = sorted[Math.floor(sorted.length/2)];
        setCalibrated(true);
      }
    }

    const closedT = baseline ? baseline * CLOSED_RATIO : 0.17;

    // PERCLOS (60s window)
    const hist = earHistory.current;
    const perclosVal = hist.length > 0 ? hist.filter(p => p.ear < closedT).length / hist.length : 0;

    // Blink rate + duration
    blinkTimestamps.current = blinkTimestamps.current.filter(t => now - t < 60000);
    blinkDurations.current  = blinkDurations.current.slice(-30);  // keep last 30
    const windowMs = Math.min(now - (hist[0]?.t || now), 60000);
    const bpm = hist.length >= 5 && windowMs > 3000
      ? blinkTimestamps.current.length * (60000 / windowMs) : null;
    const avgBlinkDur = blinkDurations.current.length > 0
      ? Math.round(blinkDurations.current.reduce((s,d)=>s+d,0) / blinkDurations.current.length) : 0;
    const blinkVar = blinkDurations.current.length > 2
      ? Math.round(Math.sqrt(blinkDurations.current.reduce((s,d,_,a)=>s+Math.pow(d-a.reduce((s2,d2)=>s2+d2,0)/a.length,2),0)/blinkDurations.current.length))
      : 0;

    // Yawn / squint sustained states
    if (mar > MAR_YAWN) yawnFrames.current++; else yawnFrames.current = 0;
    const yawning = yawnFrames.current >= YAWN_FRAMES_MIN;
    if (yawning && yawnFrames.current === YAWN_FRAMES_MIN) {
      yawnCountRef.current++;
      snapshotYawnRef.current++;
      setYawnCount(yawnCountRef.current);
    }

    const squintCheck = earAvg >= (baseline||0.28)*0.68 && earAvg < (baseline||0.28)*0.86;
    if (squintCheck) squintFrames.current++; else squintFrames.current = 0;
    const squinting = squintFrames.current >= SQUINT_FRAMES;
    // Blendshape-augmented signals (bs is null unless blendshapes model is loaded)
    const cheekPuff      = bs ? ((bs.cheekPuff||0) > 0.3) : false;
    const eyeSquintBS    = bs ? (((bs.eyeSquintLeft||0) + (bs.eyeSquintRight||0))/2 > 0.4) : false;
    const squintingFinal = squinting || eyeSquintBS;

    // Distraction / focus from head pose
    const profile = DISTRACT_PROFILES[monitorSetup] || DISTRACT_PROFILES.single;
    const isDistracted = Math.abs(pose.yaw) > profile.yaw || pose.pitch > profile.pitch;
    const gazeOnScreen = !isDistracted;
    const newFocus = isDistracted ? 'distracted' : 'focused';
    if (lastFocusRef.current !== null && lastFocusRef.current !== newFocus) {
      const elapsed = now - (newFocus === 'focused' ? distractStartRef.current : focusStartRef.current) || 0;
      if (lastFocusRef.current === 'focused') totalFocusRef.current += elapsed;
      else totalDistractRef.current += elapsed;
    }
    if (newFocus === 'focused'    && lastFocusRef.current !== 'focused')    focusStartRef.current = now;
    if (newFocus === 'distracted' && lastFocusRef.current !== 'distracted') distractStartRef.current = now;
    lastFocusRef.current = newFocus;
    const totalTracked = totalFocusRef.current + totalDistractRef.current;
    const pct = totalTracked > 5000 ? Math.round(totalFocusRef.current / totalTracked * 100) : null;
    setFocusState(newFocus);
    setFocusPct(pct);
    setFocusedMs(totalFocusRef.current);
    setDistractedMs(totalDistractRef.current);

    // Energy
    const energyLevel = getEnergyLevel(earAvg, baseline, perclosVal, bpm);

    // Attention score
    const attention = calcAttentionScore({
      gazeOnScreen, blinkRate: bpm||12, perclos: perclosVal,
      headYaw: pose.yaw, headPitch: pose.pitch, monitorSetup,
      facePresent: true, blinkDurationAvg: avgBlinkDur,
    });

    // Emotion — throttle face-api to every 3s (expensive)
    let currentEmotion = lastEmotionRef.current?.emotion || 'neutral';
    let currentExpressions = lastEmotionRef.current?.expressions || null;
    const faceapi = faceapiRef.current;
    if (faceapi && (!lastEmotionRef.current || now - lastEmotionRef.current.t > 3000)) {
      // Run async, don't block the frame loop
      (async () => {
        try {
          const r = await faceapi.detectSingleFace(videoEl, new faceapi.TinyFaceDetectorOptions()).withFaceExpressions();
          if (r) {
            const faceApiEmotion = Object.entries(r.expressions).reduce((a,b) => a[1]>b[1]?a:b)[0];
            const geometricEmotion = classifyEmotion({
              faceApiEmotion, expressions: r.expressions, brow, lip, jaw, chin, gaze: gazeResult,
              ear: earAvg, baseline, bpm, perclos: perclosVal, yawning, squinting: squintingFinal,
              singleScreen: monitorSetup === 'single', cheekPuff,
              headYaw: pose.yaw, monitorSetup,
            });
            lastEmotionRef.current = { emotion: geometricEmotion, expressions: r.expressions, faceApiEmotion, t: now };
            setExpressions(r.expressions);
          }
        } catch(e) {}
      })();
    } else {
      // Re-classify geometry with latest data even without new face-api run
      const faceApiEmotion = lastEmotionRef.current?.faceApiEmotion || 'neutral';
      currentEmotion = classifyEmotion({
        faceApiEmotion, expressions: currentExpressions || {}, brow, lip, jaw, chin, gaze: gazeResult,
        ear: earAvg, baseline, bpm, perclos: perclosVal, yawning, squinting: squintingFinal,
        singleScreen: monitorSetup === 'single', cheekPuff,
        headYaw: pose.yaw, monitorSetup,
      });
      setEmotion(currentEmotion);
    }

    // ── Emotion smoothing ─────────────────────────────────────────────────────
    // Buffer last 10 readings, only switch if new emotion wins >40% of frames
    // AND face-api confidence for raw expression is >55% (confidence threshold)
    const rawConfidence = lastEmotionRef.current?.expressions
      ? Math.max(...Object.values(lastEmotionRef.current.expressions))
      : 0;
    const CONFIDENCE_THRESHOLD = 0.55;  // ignore weak face-api readings
    const BUFFER_SIZE = 10;             // ~3s window at face-api 3s interval + geo frames
    const WIN_THRESHOLD = 0.40;         // need 40% of buffer to confirm switch

    // Only push to buffer if we have a confident reading or it's geometry-based
    const isGeometryEmotion = ['concentrating','thinking','calm','tense','zoned_out',
      'stressed','dejected','squinting','smirking','disgusted','yawning'].includes(currentEmotion);
    if (isGeometryEmotion || rawConfidence >= CONFIDENCE_THRESHOLD) {
      emotionBufferRef.current.push(currentEmotion);
    }
    if (emotionBufferRef.current.length > BUFFER_SIZE) emotionBufferRef.current.shift();

    const bufCounts = {};
    emotionBufferRef.current.forEach(e => { bufCounts[e] = (bufCounts[e]||0)+1; });
    const topEmotion = Object.entries(bufCounts).sort((a,b)=>b[1]-a[1])[0]?.[0];
    const topRatio = topEmotion ? bufCounts[topEmotion] / emotionBufferRef.current.length : 0;

    // Only switch stable emotion if new winner holds ≥40% of recent frames
    if (topEmotion && topRatio >= WIN_THRESHOLD) {
      stableEmotionRef.current = topEmotion;
    }
    const smoothedEmotion = stableEmotionRef.current || currentEmotion;
    setEmotion(smoothedEmotion);

    // Update UI state
    setEar(earAvg);
    setPerclos(perclosVal);
    setBlinkRate(bpm);
    setBlinkDurationAvg(avgBlinkDur);
    // Smooth energy — only switch if same value held for 4+ consecutive frames
    energyBufferRef.current.push(energyLevel);
    if (energyBufferRef.current.length > 6) energyBufferRef.current.shift();
    const energyCounts = {};
    energyBufferRef.current.forEach(e => { energyCounts[e] = (energyCounts[e]||0)+1; });
    const topEnergy = Object.entries(energyCounts).sort((a,b)=>b[1]-a[1])[0]?.[0];
    if (topEnergy && energyCounts[topEnergy] >= 4) setEnergy(topEnergy);

    // Smooth attention — rolling average of last 8 frames to prevent jumping
    attnBufferRef.current.push(attention);
    if (attnBufferRef.current.length > 8) attnBufferRef.current.shift();
    const smoothedAttn = Math.round(
      attnBufferRef.current.reduce((a,b) => a+b, 0) / attnBufferRef.current.length
    );
    setAttentionScore(smoothedAttn);
    setGaze(gazeResult);
    setHeadPose(pose);

    // Store latest for snapshot builder
    latestMetrics.current = {
      emotion: currentEmotion, energy: energyLevel,
      attentionScore: attention, blinkRate: bpm, blinkDurationAvg: avgBlinkDur,
      blinkVariability: blinkVar, perclos: perclosVal,
      gazeOnScreen, headYaw: pose.yaw, headPitch: pose.pitch, facePresent: true,
    };

    // ── 60-second snapshot ────────────────────────────────────────────────
    snapshotWindowRef.current.push(latestMetrics.current);
    if (now - lastSnapshotRef.current >= 60000 && snapshotWindowRef.current.length > 0) {
      const window = snapshotWindowRef.current;
      const avgVal = key => window.reduce((s,f) => s+(f[key]||0), 0) / window.length;
      const modVal = key => {
        const counts = {};
        window.forEach(f => { const v = f[key]; if(v) counts[v]=(counts[v]||0)+1; });
        return Object.entries(counts).sort((a,b)=>b[1]-a[1])[0]?.[0] || null;
      };
      const snapshot = {
        timestamp: new Date().toISOString(),
        emotion: modVal('emotion'),
        emotionConfidence: null,   // filled by face-api if available
        attentionScore:   Math.round(avgVal('attentionScore')),
        blinkRate:        +(avgVal('blinkRate')).toFixed(1),
        blinkDurationAvg: Math.round(avgVal('blinkDurationAvg')),
        blinkVariability: Math.round(avgVal('blinkVariability')),
        perclos:          +(avgVal('perclos')).toFixed(3),
        gazeOnScreen:     window.filter(f=>f.gazeOnScreen).length / window.length > 0.5,
        headYaw:          +(avgVal('headYaw')).toFixed(2),
        yawns:            snapshotYawnRef.current,
        facePresent:      window.filter(f=>f.facePresent).length / window.length > 0.5,
      };
      if (onSnapshot) onSnapshot(snapshot);
      snapshotWindowRef.current = [];
      snapshotYawnRef.current   = 0;
      lastSnapshotRef.current   = now;
    }

    // ── Canvas overlay ────────────────────────────────────────────────────
    drawMesh(ctx, lm, canvasEl.width, canvasEl.height, {
      earAvg, baseline, yawning, squinting: squintingFinal, isDistracted,
      gaze: gazeResult, brow, lip, jaw, chin, pose, bs,
      emotion: currentEmotion, energy: energyLevel, attention, showMesh,
    });
    ctx.restore();

    // Emit live metrics for parent
    if (onLiveMetrics) {
      onLiveMetrics({ ear: earAvg, blinkCount: blinkCountRef.current, blinkRate: bpm,
        blinkDurationAvg: avgBlinkDur, perclos: perclosVal, attention, energy: energyLevel,
        gazeOnScreen, headPose: pose });
    }

  }, [monitorSetup, onSnapshot, onLiveMetrics]);

  // ── RAF loop ──────────────────────────────────────────────────────────────
  const showMeshRef = useRef(false);
  const setMeshVisible = useCallback((v) => { showMeshRef.current = v; }, []);

  const startLoop = useCallback((videoEl, canvasEl) => {
    let ts = 0;
    function loop(t) {
      ts = t;
      runFrame(videoEl, canvasEl, t, showMeshRef.current);
      rafRef.current = requestAnimationFrame(loop);
    }
    rafRef.current = requestAnimationFrame(loop);
  }, [runFrame]);

  const stopLoop = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  }, []);

  const reset = useCallback(() => {
    earHistory.current = []; blinkTimestamps.current = []; blinkDurations.current = [];
    eyeState.current = 'open'; blinkOpenT.current = null; blinkCountRef.current = 0;
    calibSamples.current = []; baselineRef.current = null;
    yawnFrames.current = 0; squintFrames.current = 0; yawnCountRef.current = 0;
    focusStartRef.current = null; distractStartRef.current = null;
    // Restore focus/distract ms — prefer exact localStorage value, fall back to snapshot estimate
    try {
      const focusKey = 'moodscope_focus_ms_v1';
      const saved = JSON.parse(localStorage.getItem(focusKey) || 'null');
      const today = new Date().toDateString();
      if (saved && saved.date === today && saved.focusMs != null) {
        // Exact ms from last save (at most 10s stale)
        totalFocusRef.current    = saved.focusMs;
        totalDistractRef.current = saved.distractMs;
      } else {
        // Fall back to snapshot estimate (1 min per snapshot)
        const snaps = JSON.parse(localStorage.getItem('moodscope_snapshots_v1') || '[]');
        const todaySnaps = snaps.filter(s => new Date(s.timestamp).toDateString() === today);
        totalFocusRef.current    = todaySnaps.filter(s => (s.attentionScore||0) >= 60).length * 60000;
        totalDistractRef.current = todaySnaps.filter(s => (s.attentionScore||0) < 60).length  * 60000;
      }
    } catch(e) {
      totalFocusRef.current = 0; totalDistractRef.current = 0;
    }
    lastFocusRef.current = null;

    // Save focus ms to localStorage every 10 seconds
    focusSaveRef.current = setInterval(() => {
      try {
        localStorage.setItem('moodscope_focus_ms_v1', JSON.stringify({
          date:       new Date().toDateString(),
          focusMs:    totalFocusRef.current,
          distractMs: totalDistractRef.current,
          savedAt:    Date.now(),
        }));
      } catch(e) {}
    }, 10000);
    snapshotWindowRef.current = []; lastSnapshotRef.current = Date.now(); snapshotYawnRef.current = 0;
    lastEmotionRef.current = null; emotionBufferRef.current = []; stableEmotionRef.current = null; latestMetrics.current = {};
    energyBufferRef.current = []; attnBufferRef.current = [];
    if (focusSaveRef.current) { clearInterval(focusSaveRef.current); focusSaveRef.current = null; }
    setFacePresent(false); setEmotion(null); setExpressions(null); setEnergy(null);
    setAttentionScore(null); setEar(null); setBlinkCount(0); setBlinkRate(null);
    setBlinkDurationAvg(null); setPerclos(0); setGaze(null); setHeadPose(null);
    setFocusState(null); setFocusPct(null); setFocusedMs(0); setDistractedMs(0);
    setCalibrated(false); setCalibProgress(0); setYawnCount(0);
  }, []);

  return {
    init, startLoop, stopLoop, reset, ready, setMeshVisible,
    facePresent, emotion, expressions, energy, attentionScore,
    ear, blinkCount, blinkRate, blinkDurationAvg, perclos,
    gaze, headPose, focusState, focusPct, focusedMs, distractedMs,
    calibrated, calibProgress, yawnCount,
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// CANVAS MESH RENDERER
// Draws MediaPipe face mesh + all tracked signal overlays
// ═══════════════════════════════════════════════════════════════════════════════
const EMOTION_COLORS = {
  happy:'#0cb8a0', calm:'#3b82f6', concentrating:'#0a9688', thinking:'#f59e0b',
  sad:'#6366f1', stressed:'#ef4444', surprised:'#f59e0b', fearful:'#8b5cf6',
  disgusted:'#14b8a6', angry:'#ef4444', neutral:'#9ca3af', yawning:'#9060c0',
  zoned_out:'#606070', smirking:'#f0d080', tense:'#f06840', dejected:'#8090c0', squinting:'#d0c060',
};
const ENERGY_COLORS = { sleepy:'#9060c0', tired:'#c09060', alert:'#60d4f0', energetic:'#60f0c8' };
const EMOJI_MAP = {
  happy:'😊', calm:'😌', concentrating:'🧐', thinking:'🤔', sad:'😢', stressed:'😤',
  surprised:'😲', fearful:'😨', disgusted:'🤢', angry:'😠', neutral:'😐', yawning:'🥱',
  zoned_out:'😶', smirking:'😏', tense:'😬', dejected:'😔', squinting:'🤨',
};
const ENERGY_EMOJI = { sleepy:'😴', tired:'😑', alert:'😐', energetic:'⚡' };

// MediaPipe face mesh connections (subset — eyes, brows, lips, jaw)
const MESH_CONNECTIONS = [
  // Jawline
  [10,338,297,332,284,251,389,356,454,323,361,288,397,365,379,378,400,377,152,148,176,149,150,136,172,58,132,93,234,127,162,21,54,103,67,109,10],
  // Left eye
  [33,246,161,160,159,158,157,173,133,155,154,153,145,144,163,7,33],
  // Right eye
  [362,398,384,385,386,387,388,466,263,249,390,373,374,380,381,382,362],
  // Left brow
  [46,53,52,65,55,70,63,105,66,107],
  // Right brow
  [276,283,282,295,285,300,293,334,296,336],
  // Lips outer
  [61,185,40,39,37,0,267,269,270,409,291,375,321,405,314,17,84,181,91,146,61],
  // Nose bridge
  [168,6,197,195,5,4,1,19,94],
];


// MediaPipe canonical face mesh tessellation connections (subset of full 468-point triangulation)
const TESSELATION = [
  [127,34],[34,139],[139,127],[11,0],[0,37],[37,11],[232,231],[231,120],[120,232],
  [72,37],[37,39],[39,72],[128,121],[121,47],[47,128],[232,121],[121,128],[128,232],
  [104,69],[69,67],[67,104],[175,171],[171,148],[148,175],[157,154],[154,155],[155,157],
  [118,50],[50,101],[101,118],[73,39],[39,40],[40,73],[9,151],[151,108],[108,9],
  [48,115],[115,131],[131,48],[194,204],[204,211],[211,194],[74,40],[40,185],[185,74],
  [80,42],[42,183],[183,80],[40,92],[92,186],[186,40],[230,229],[229,118],[118,230],
  [202,212],[212,214],[214,202],[83,18],[18,17],[17,83],[76,61],[61,146],[146,76],
  [160,29],[29,30],[30,160],[56,157],[157,173],[173,56],[106,204],[204,194],[194,106],
  [135,214],[214,192],[192,135],[203,165],[165,98],[98,203],[21,54],[54,103],[103,21],
  [67,69],[69,104],[104,67],[109,108],[108,151],[151,109],[48,64],[64,235],[235,48],
  [100,47],[47,121],[121,100],[210,202],[202,214],[214,210],[169,150],[150,149],[149,169],
  [139,126],[126,47],[47,139],[38,72],[72,41],[41,38],[212,202],[202,204],[204,212],
  [57,43],[43,61],[61,57],[186,92],[92,165],[165,186],[189,190],[190,243],[243,189],
  [145,144],[144,23],[23,145],[35,111],[111,31],[31,35],[143,111],[111,137],[137,143],
  [123,116],[116,117],[117,123],[44,19],[19,94],[94,44],[51,3],[3,45],[45,51],
  [208,201],[201,32],[32,208],[171,175],[175,199],[199,171],[81,82],[82,38],[38,81],
  [53,46],[46,225],[225,53],[144,163],[163,110],[110,144],[52,65],[65,55],[55,52],
  [26,22],[22,24],[24,26],[112,26],[26,23],[23,112],[189,243],[243,244],[244,189],
  [141,19],[19,102],[102,141],[181,91],[91,146],[146,181],[1,2],[2,97],[97,1],
  [206,203],[203,36],[36,206],[221,56],[56,28],[28,221],[108,107],[107,55],[55,108],
  [31,229],[229,20],[20,31],[2,1],[1,187],[187,2],[141,125],[125,241],[241,141],
  [210,169],[169,170],[170,210],[140,170],[170,169],[169,140],[241,125],[125,142],[142,241],
  [22,26],[26,112],[112,22],[9,0],[0,11],[11,9],[130,247],[247,33],[33,130],
  [72,11],[11,37],[37,72],[228,31],[31,229],[229,228],[175,156],[156,171],[171,175],
  [46,53],[53,52],[52,46],[143,22],[22,26],[26,143],[216,206],[206,203],[203,216],
  [183,42],[42,184],[184,183],[210,211],[211,170],[170,210],[140,169],[169,150],[150,140],
  [92,186],[186,165],[165,92],[84,17],[17,16],[16,84],[15,85],[85,84],[84,15],
  [180,90],[90,85],[85,180],[20,229],[229,31],[31,20],[87,14],[14,16],[16,87],
  [26,143],[143,22],[22,26],[101,100],[100,120],[120,101],[63,105],[105,104],[104,63],
  [93,137],[137,132],[132,93],[76,62],[62,96],[96,76],[62,77],[77,96],[96,62],
  [91,96],[96,77],[77,91],[62,96],[96,77],[77,62],[66,107],[107,108],[108,66],
];

function drawMesh(ctx, lm, W, H, { earAvg, baseline, yawning, squinting, isDistracted, gaze, brow, lip, jaw, emotion, energy, attention, bs, showMesh }) {
  const px = (p) => ({ x: p.x * W, y: p.y * H });
  const ec = EMOTION_COLORS[emotion] || '#a0a0b0';
  const en = ENERGY_COLORS[energy]   || '#60d4f0';

  // ── Full 468-point tessellation (when showMesh enabled) ─────────────────────
  if (showMesh) {
    ctx.lineWidth = 0.5;
    TESSELATION.forEach(([i, j]) => {
      if (!lm[i] || !lm[j]) return;
      ctx.beginPath();
      ctx.moveTo(lm[i].x * W, lm[i].y * H);
      ctx.lineTo(lm[j].x * W, lm[j].y * H);
      ctx.strokeStyle = `${ec}20`;
      ctx.stroke();
    });
    // All 468 landmark dots
    ctx.fillStyle = `${ec}35`;
    lm.forEach(p => {
      ctx.beginPath();
      ctx.arc(p.x * W, p.y * H, 0.8, 0, Math.PI * 2);
      ctx.fill();
    });
  }

  // ── Draw feature connections (always visible) ─────────────────────────────
  ctx.lineWidth = 0.8;
  MESH_CONNECTIONS.forEach(conn => {
    ctx.beginPath();
    const start = px(lm[conn[0]]);
    ctx.moveTo(start.x, start.y);
    conn.slice(1).forEach(i => { const p = px(lm[i]); ctx.lineTo(p.x, p.y); });
    ctx.strokeStyle = isDistracted ? 'rgba(240,96,96,0.25)' : `${ec}30`;
    ctx.stroke();
  });

  // ── Landmark dots (sparse — key points only) ──────────────────────────────
  ctx.fillStyle = `${ec}60`;
  [1,4,10,152,234,454, 33,133,362,263, 61,291].forEach(i => {
    const p = px(lm[i]);
    ctx.beginPath(); ctx.arc(p.x, p.y, 1.5, 0, Math.PI*2); ctx.fill();
  });

  // ── Brow state colour ─────────────────────────────────────────────────────
  const browColor = brow.browLift < 0.11 ? '#f08040' : brow.browLift > 0.20 ? '#f0c060' : `${ec}80`;
  ctx.strokeStyle = browColor; ctx.lineWidth = 2;
  [[46,53,52,65,55],[276,283,282,295,285]].forEach(idxs => {
    const pts = idxs.map(i => px(lm[i]));
    ctx.beginPath(); ctx.moveTo(pts[0].x, pts[0].y);
    pts.slice(1).forEach(p => ctx.lineTo(p.x, p.y)); ctx.stroke();
  });

  // ── Eye outlines — colour by state ────────────────────────────────────────
  const eyeColor = squinting ? '#f0d060' : en;
  ctx.strokeStyle = eyeColor; ctx.lineWidth = squinting ? 2 : 1.5;
  [[33,246,161,160,159,158,157,173,133,155,154,153,145,144,163,7,33],
   [362,398,384,385,386,387,388,466,263,249,390,373,374,380,381,382,362]].forEach(idxs => {
    const pts = idxs.map(i => px(lm[i]));
    ctx.beginPath(); ctx.moveTo(pts[0].x, pts[0].y);
    pts.slice(1).forEach(p => ctx.lineTo(p.x, p.y)); ctx.stroke();
  });

  // ── Mouth — glow when yawning ─────────────────────────────────────────────
  const mouthColor = yawning ? '#9060c0' : lip.smileScore > 0.015 ? '#c8f06070' : `${ec}30`;
  ctx.strokeStyle = mouthColor; ctx.lineWidth = yawning ? 2.5 : 1.5;
  const mouthPts = [61,185,40,39,37,0,267,269,270,409,291,375,321,405,314,17,84,181,91,146,61].map(i => px(lm[i]));
  ctx.beginPath(); ctx.moveTo(mouthPts[0].x, mouthPts[0].y);
  mouthPts.slice(1).forEach(p => ctx.lineTo(p.x, p.y)); ctx.stroke();

  // ── Distraction badge ─────────────────────────────────────────────────────
  if (isDistracted) {
    ctx.font = 'bold 11px Nunito, monospace';
    const dl = '👀 DISTRACTED'; const dw = ctx.measureText(dl).width + 14;
    ctx.fillStyle = 'rgba(240,96,96,0.9)';
    ctx.fillRect(W - dw - 8, 8, dw, 22);
    ctx.fillStyle = '#fff'; ctx.fillText(dl, W - dw - 1, 23);
  }

  // ── Attention score arc ───────────────────────────────────────────────────
  if (attention !== null) {
    const cx = 32, cy = 32, r = 22;
    ctx.strokeStyle = 'rgba(255,255,255,0.08)'; ctx.lineWidth = 4;
    ctx.beginPath(); ctx.arc(cx, cy, r, -Math.PI/2, Math.PI*1.5); ctx.stroke();
    const col = attention >= 70 ? en : attention >= 40 ? '#f0c060' : '#f06060';
    ctx.strokeStyle = col; ctx.lineWidth = 4;
    ctx.beginPath(); ctx.arc(cx, cy, r, -Math.PI/2, -Math.PI/2 + (attention/100)*Math.PI*2); ctx.stroke();
    ctx.font = 'bold 10px Nunito, monospace'; ctx.fillStyle = col; ctx.textAlign = 'center';
    ctx.fillText(attention, cx, cy + 4); ctx.textAlign = 'left';
    ctx.font = '8px Nunito, monospace'; ctx.fillStyle = 'rgba(255,255,255,0.4)';
    ctx.fillText('ATTN', cx - 10, cy + 42);
  }

  // ── Emotion label ─────────────────────────────────────────────────────────
  if (emotion) {
    const label = `${EMOJI_MAP[emotion]||'😐'} ${emotion}  ${ENERGY_EMOJI[energy]||''} ${energy||''}`;
    ctx.font = '11px Nunito, monospace';
    const tw = ctx.measureText(label).width + 14;
    ctx.fillStyle = 'rgba(10,10,15,0.85)'; ctx.fillRect(8, H - 30, tw, 22);
    ctx.fillStyle = ec; ctx.fillText(label, 15, H - 14);
  }

  // ── Stats strip ───────────────────────────────────────────────────────────
  const stats = `EAR ${earAvg?.toFixed(3)||'—'}  brow ${brow?.browLift?.toFixed(3)||'—'}  smile ${lip?.smileScore?.toFixed(3)||'—'}`;
  ctx.font = '9px Nunito, monospace';
  const sw = ctx.measureText(stats).width + 10;
  ctx.fillStyle = 'rgba(10,10,15,0.7)'; ctx.fillRect(8, H - 52, sw, 16);
  ctx.fillStyle = en; ctx.fillText(stats, 13, H - 40);
}
